import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, OutlinedInput, Select, ListSubheader, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import GenericTextField from './GenericTextField/GenericTextField';
import GenericButton, { GenericButtonTypeEnum } from './GenericButton';
import { ReactComponent as ImportantIcon } from '../assets/important-icon.svg';
import { useTranslation } from 'react-i18next';

import { palette } from '../theme';
interface GenericDateRangeFilterProps {
  label: string;
  from?: string;
  to?: string;
  onShowResults: (range: { from?: string; to?: string }) => void;
  disabled?: boolean;
}

const GenericDateRangeFilter: React.FC<GenericDateRangeFilterProps> = ({ label, from, to, onShowResults, disabled }) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState<Dayjs | undefined>(from ? dayjs(from) : undefined);
  const [toDate, setToDate] = useState<Dayjs | undefined>(to ? dayjs(to) : undefined);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (fromDate && toDate && fromDate.isAfter(toDate)) {
      setErrorMessage(t('dateRange.fromAfterTo'));
    } else {
      setErrorMessage('');
    }
  }, [fromDate, toDate, t]);

  const labelLength = label.length;
  const baseWidth = 60;
  const calcWidth = baseWidth + labelLength * 8;

  const handleApply = () => {
    if (errorMessage) return;
    const range = {
      from: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
      to: toDate ? toDate.format('YYYY-MM-DD') : undefined
    };
    onShowResults(range);
  };

  const handleClear = () => {
    setFromDate(undefined);
    setToDate(undefined);
    const cleared = { from: undefined, to: undefined };
    onShowResults(cleared);
  };

  return (
    <FormControl sx={{ width: 'auto' }}>
      <InputLabel
        disabled={disabled}
        shrink={false}
        sx={{
          left: '16px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 400 }}>
          {label}
        </Typography>
      </InputLabel>
      <Select
        value=""
        input={<OutlinedInput notched={false} />}
        renderValue={() => ''}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: '40px',
          padding: '6px 16px',
          flex: '1 0 0',
          borderRadius: '10px',
          // the outline is the theme's, like every other field; a second border here doubled it
          minWidth: calcWidth + 8,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box'
        }}
        MenuProps={{
          PaperProps: { sx: { borderRadius: '10px', p: 1 } }
        }}
      >
        <ListSubheader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
            <GenericTextField
              label={t('dateRange.from')}
              isDate
              value={fromDate?.format('YYYY-MM-DD') || ''}
              onChange={e => setFromDate(e.target.value ? dayjs(e.target.value) : undefined)}
            />
            <GenericTextField
              label={t('dateRange.to')}
              isDate
              value={toDate?.format('YYYY-MM-DD') || ''}
              onChange={e => setToDate(e.target.value ? dayjs(e.target.value) : undefined)}
            />
          </Box>
          {errorMessage && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ImportantIcon />
              <Typography sx={{ fontSize: '14px', ml: '6px' }}>{errorMessage}</Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: `1px solid ${palette.line}`
            }}
          >
            <GenericButton
              sx={{ color: palette.error.main }}
              buttonType={GenericButtonTypeEnum.Neutral}
              label={t('common.clearAll')}
              variant="text"
              onClick={handleClear}
            />
            <GenericButton
              buttonType={GenericButtonTypeEnum.Neutral}
              label={t('common.showResults')}
              variant="text"
              onClick={handleApply}
            />
          </Box>
        </ListSubheader>
      </Select>
    </FormControl>
  );
};

export default GenericDateRangeFilter;
